/**
 * @file The incremental insert of a Signify fetch job's fetched parts into the open table.
 * Consumer: `SignifyFetchJobProgress`, whose Insert button runs it and whose label reads the
 * `inserting` proxy this hook takes from `signifyFetchJobInsertingFor`.
 * Three passes: build the row payloads through a pool of eight, ask the table in one batched query
 * which identities it already holds, then write and stamp sequentially in the operator's order.
 * Four invariants carry the correctness. The run state is keyed by job rather than by mount, so
 * closing the modal mid-run cannot let a second press duplicate the rows the first has not written.
 * The held set the loop tests is the table's answer plus every identity written since: a part whose
 * row this run just wrote is held for the rest of it, so two inputs resolving to one product — a UPC
 * and the same product's 12NC — yield one row and two stamps rather than two rows.
 * The row is written BEFORE `insertedAt` is stamped, so a crash between them costs a stamp the next
 * press repairs rather than a row nobody can see. And the stamp is a raw `updateDoc`:
 * `firestore.rules` permits a change to `insertedAt` and nothing else, and the store's `update`
 * would add `modifiedAt` and be refused every time.
 * A refused stamp is a failure, not a silent revert: unreported it would leave the part `ready`
 * forever and every later press would say nothing was wrong.
 * Everything the run needs is read off the parts proxy before the first await; the only proxy
 * touches afterwards are writes.
 */

import { useCallback, useContext } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { signifyFetchJobPartsPath } from '#types';
import type { ProductAndPriceAndAvailabilityResponse, SignifyFetchPart } from '#types';
import type { EntityRecord, Fields } from '#types/System.types';
import { MessageContext } from '@/providers/MessageContext';
import { firestore } from '@/services/startFirestore';
import type { CollectionStore } from '@/store';
import { useCreateLineItemFromSignify } from '../../SignifyProductModal/use/useCreateLineItemFromSignify';
import { signifyFetchJobInsertingFor } from './services';
import type { SignifyFetchJobModalProps } from '../index.types';

/** `buildLineItemFields` reads a Manufacturers record per part, so a thousand serial gets is a minute of latency. */
const BUILD_POOL_SIZE = 8;
const NAMED_FAILURE_LIMIT = 3;

/** The row is in the table but the stamp was refused, so the next press offers the part again. */
const stampFailure = (input: string): { input: string; error: string } => ({
  input,
  error: 'Its row is in the table but the part could not be marked as inserted',
});

type ReadyPart = {
  partId: string;
  input: string;
  result: ProductAndPriceAndAvailabilityResponse;
  quantity: number;
};

type BuiltPart = ReadyPart &
  (
    | { kind: 'record'; identity: string; recordId: string; duplicate: boolean }
    | {
        kind: 'manual';
        identity: string;
        recordName: string;
        fields: Fields;
        entityReferences: EntityRecord['entityReferences'];
      }
  );

type UseSignifyFetchJobInsertParams = {
  jobId: string;
  partsStore: CollectionStore<SignifyFetchPart>;
  saveRecord: boolean;
  disabled: boolean;
  onSuccess: SignifyFetchJobModalProps['onSuccess'];
  onManualInsert: SignifyFetchJobModalProps['onManualInsert'];
  onFindInsertedRows: SignifyFetchJobModalProps['onFindInsertedRows'];
};

export const useSignifyFetchJobInsert = ({
  jobId,
  partsStore,
  saveRecord,
  disabled,
  onSuccess,
  onManualInsert,
  onFindInsertedRows,
}: UseSignifyFetchJobInsertParams) => {
  const inserting = signifyFetchJobInsertingFor(jobId);
  const { messageApi } = useContext(MessageContext);
  const { buildLineItemFields, createLineItemRecord } = useCreateLineItemFromSignify();

  /**
   * Optimistic first so the Insert count drops at once, then the one field the rules let a client
   * write. A refused stamp puts the part back in `ready`, where the next press finds its row
   * already there and stamps without inserting again — and resolves false, so the run counts it as
   * a failure and names it rather than reporting a success the operator cannot see.
   */
  const stampInsertedAt = useCallback(async (partId: string): Promise<boolean> => {
    const at = Date.now();
    const entry = partsStore.state.docs.get(partId);
    if (entry) entry.data.insertedAt = at;

    try {
      await updateDoc(doc(firestore, signifyFetchJobPartsPath(jobId), partId), { insertedAt: at });
      return true;
    } catch (error) {
      const reverted = partsStore.state.docs.get(partId);
      if (reverted) reverted.data.insertedAt = null;
      console.error('[useSignifyFetchJobInsert] Failed to stamp insertedAt', error);
      return false;
    }
  }, [jobId, partsStore]);

  const runInsert = useCallback(async (): Promise<void> => {
    if (disabled || inserting.phase !== 'idle') return;

    const ready = partsStore.state.docIds.flatMap<ReadyPart>((partId) => {
      const part = partsStore.state.docs.get(partId)?.data;
      if (part === undefined || part.state !== 'done' || part.insertedAt !== null) return [];
      return [{ partId: part.docId, input: part.input, result: part.result, quantity: part.quantity }];
    });
    if (ready.length === 0) return;

    const asRecords = saveRecord && onSuccess !== undefined;
    const built: Array<BuiltPart | undefined> = new Array<BuiltPart | undefined>(ready.length);
    const failures: Array<{ input: string; error: string }> = [];
    const cursor = { index: 0 };
    const counters = { inserted: 0, duplicates: 0 };

    inserting.phase = 'preparing';
    inserting.total = ready.length;
    inserting.done = 0;

    const buildWorker = async (): Promise<void> => {
      for (;;) {
        const index = cursor.index;
        cursor.index += 1;
        if (index >= ready.length) return;

        const part = ready[index];
        try {
          if (asRecords) {
            const { recordId, status } = await createLineItemRecord(part.result);
            built[index] = {
              ...part,
              kind: 'record',
              identity: recordId,
              recordId,
              duplicate: status === 'duplicate',
            };
          } else {
            const { fields, recordName, entityReferences } = await buildLineItemFields(part.result);
            const productId = fields.manufacturersProductId?.value;
            if (typeof productId !== 'string' || productId.length === 0) {
              throw new Error('The mapped fields carry no manufacturers product id');
            }
            built[index] = { ...part, kind: 'manual', identity: productId, recordName, fields, entityReferences };
          }
        } catch (error) {
          failures.push({
            input: part.input,
            error: error instanceof Error ? error.message : 'Unknown error',
          });
        }
        inserting.done += 1;
      }
    };

    try {
      await Promise.all(Array.from({ length: BUILD_POOL_SIZE }, () => buildWorker()));

      const prepared = built.flatMap((entry) => (entry ? [entry] : []));
      const existing = onFindInsertedRows
        ? await onFindInsertedRows({
            productIds: prepared.flatMap((entry) => (entry.kind === 'manual' ? [entry.identity] : [])),
            recordIds: prepared.flatMap((entry) => (entry.kind === 'record' ? [entry.identity] : [])),
          })
        : { productIds: new Set<string>(), recordIds: new Set<string>() };
      const held = {
        productIds: new Set(existing.productIds),
        recordIds: new Set(existing.recordIds),
      };

      inserting.phase = 'inserting';
      inserting.total = prepared.length;
      inserting.done = 0;

      for (const entry of prepared) {
        if (entry.kind === 'record' && entry.duplicate) counters.duplicates += 1;

        const alreadyHeld = entry.kind === 'manual'
          ? held.productIds.has(entry.identity)
          : held.recordIds.has(entry.identity);

        if (alreadyHeld) {
          if (!(await stampInsertedAt(entry.partId))) failures.push(stampFailure(entry.input));
          inserting.done += 1;
          continue;
        }

        const rowId = entry.kind === 'record'
          ? (await onSuccess?.(entry.recordId, { quantity: entry.quantity })) ?? null
          : (await onManualInsert?.(
              entry.recordName,
              entry.fields,
              entry.entityReferences,
              { quantity: entry.quantity },
            )) ?? null;

        if (rowId === null) {
          failures.push({ input: entry.input, error: 'The row was not written' });
          inserting.done += 1;
          continue;
        }

        counters.inserted += 1;
        const heldIds = entry.kind === 'manual' ? held.productIds : held.recordIds;
        heldIds.add(entry.identity);
        if (!(await stampInsertedAt(entry.partId))) failures.push(stampFailure(entry.input));
        inserting.done += 1;
      }
    } catch (error) {
      messageApi.error(
        `The insert stopped: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
      return;
    } finally {
      inserting.phase = 'idle';
      inserting.done = 0;
      inserting.total = 0;
    }

    if (counters.duplicates > 0) {
      messageApi.warning(
        `${counters.duplicates} parts matched more than one Line Item record — please review your records`,
      );
    }
    if (failures.length > 0) {
      const named = failures
        .slice(0, NAMED_FAILURE_LIMIT)
        .map((failure) => `${failure.input} — ${failure.error}`)
        .join('; ');
      messageApi.error(
        `${failures.length} ${failures.length === 1 ? 'part' : 'parts'} did not finish inserting: ${named}`,
      );
      return;
    }
    if (counters.inserted === 0) {
      messageApi.info('Every fetched part was already in this table');
      return;
    }
    messageApi.success(`${counters.inserted} ${counters.inserted === 1 ? 'part' : 'parts'} inserted`);
  }, [
    buildLineItemFields,
    createLineItemRecord,
    disabled,
    inserting,
    messageApi,
    onFindInsertedRows,
    onManualInsert,
    onSuccess,
    partsStore,
    saveRecord,
    stampInsertedAt,
  ]);

  return { inserting, runInsert };
};
